import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";

import { Link, useLocation } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";

import EditAction from "../../common/edit-action/EditAction";
import { useUrl } from "../../../hooks/urlEncoding";
import {
  RolesListColumnSortable,
  RolesListRequest,
} from "../../../services/Endpoints/roles/RolesListEndpoint";
import { RoleSchema } from "../../../services/Schemas/Role";
import { AccessEnums } from "../../../constants/accessEnum";
import { useCanAccess } from "../../../hooks/canAccess";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import useUrlSort from "../../../hooks/useUrlSort";

export interface IUsers {
  description: string;
  id: number;
  caption: string;
  operations: ReactNode | string;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
  setRoleInfo?: (payload: RoleSchema) => void;
  setOpenModal: (payload: boolean) => void;
}

export default function RolesTableComponent({
  walletReload,
  setWalletReload,
  setRoleInfo,
  setOpenModal,
}: Readonly<ISupportTabsComponent>) {
  const { parseQueryString, setParamWithSearchParams } = useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<IUsers[] | []>([]);

  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const handleView = (payload: RoleSchema) => {
    setOpenModal(true);
    setRoleInfo && setRoleInfo(payload);
  };

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new RolesListRequest(protectedInstance);
      const resp = await walletListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.acl_roles.map(
        ({ caption, description, id }) => ({
          id,
          description,
          caption,
          operations: (
            <Stack
              justifyContent="center"
              gap="3px"
              alignItems="center"
              direction="row"
            >
              <EditAction
                disabled={!can(AccessEnums.AdminAclRolesUpdate)}
                handleEdit={() =>
                  handleView({
                    id,
                    caption,
                    description,
                  })
                }
              />
            </Stack>
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [walletReload, page, rowsPerPage,location.search]);

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true
    },
    {
      key: "caption",
      label: "نام",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "description",
      label: "توضیحات",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema,columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
      ></Box>

      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
